<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import useAuthStore from '@/common/store/pinia/auth';
  import useTotpStore from '@/common/store/pinia/totp';
  import TextWithCopy from '@/common/shared/TextWithCopy.vue';
  import { totpCodeRule } from '@/common/validation/authRules';

  // eslint-disable-next-line import/no-unresolved
  import VueQrcode from 'qrcode.vue';

  type Props = {
    username?: string;
  }

  const props = defineProps<Props>();
  const authStore = useAuthStore();
  const totpStore = useTotpStore();

  const username = computed(() => props.username || authStore.currentUser?.name || '');
  const totpSecret = computed(() => totpStore.totpSecret);
  const otpAuth = computed(() => totpStore.otpAuthUrl(username.value));

  type SetupType = 'qr' | 'key';
  type EmitEvents = {
    (event: 'submit', code: string): void;
    (event: 'cancel'): void;
  }

  const emit = defineEmits<EmitEvents>();

  const formRef = ref();
  const setupType = ref<SetupType>('qr');
  const form = ref({
    verificationCode: ''
  });
  const loading = computed(() => totpStore.isLoading);
  const pending = computed(() => totpStore.isPending);
  const error = computed(() => totpStore.totpError);

  const isFormValid = computed(() => {
    if (!formRef.value?.fields) return false;
    const hasErrors = formRef.value.fields.some(field => field.validateState === 'error');
    const allValidated = formRef.value.fields.every(field => field.validateState === 'success');
    return !hasErrors && allValidated;
  });

  const rules = computed(() => ({
    verificationCode: totpCodeRule()
  }));

  const handleSubmit = async () => {
    if (pending.value) {
      return;
    }

    try {
      const isValid = await formRef.value?.validate();
      if (!isValid) {
        return;
      }

      emit('submit', form.value.verificationCode);
    } catch (err) {
      // Validation failed - errors are displayed inline by el-form-item
    }
  };

  const handleCancel = () => {
    emit('cancel');
  };

  const clearValidation = () => {
    formRef.value?.clearValidate();
    totpStore.clearError();
  };

  const resetForm = () => {
    form.value.verificationCode = '';
    setupType.value = 'qr';
    totpStore.clearError();
    clearValidation();
  };

  onMounted(() => {
    totpStore.generateSecret();
  });

  defineExpose({
    resetForm,
    clearValidation
  });
</script>

<template>
  <div
    class="two-factor-auth-setup v-two-factor-auth-setup"
    data-testid="two-factor-auth-setup"
  >
    <el-form
      ref="formRef"
      v-loading="loading || pending"
      el-loading-background="fixed-white"
      :el-loading-opacity=".8"
      :el-loading-size="48"
      :model="form"
      :rules="rules"
      @submit.prevent="handleSubmit"
    >
      <div class="username">
        {{ username }}
      </div>
      <div class="description pt-16">
        {{ $t('Use an authenticator app (for example, Google Authenticator or Microsoft Authenticator) to scan the QR code or enter the setup key manually.') }}
      </div>

      <div class="setup-type pt-24 mb-8">
        <el-radio-group v-model="setupType">
          <el-radio
            class="mr-24"
            label="qr"
            data-testid="qr-radio"
          >
            {{ $t('QR code') }}
          </el-radio>
          <el-radio
            label="key"
            data-testid="key-radio"
          >
            {{ $t('Setup key') }}
          </el-radio>
        </el-radio-group>
      </div>

      <div
        v-if="setupType === 'qr'"
        class="qr-section"
      >
        <div class="scan-instruction mt-8">
          {{ $t('Scan the QR code to add your account to the app.') }}
        </div>
        <div class="qr-code pa-24">
          <vue-qrcode
            v-if="!loading"
            :size="175"
            :value="otpAuth"
            class="qr-image"
          />
        </div>
      </div>

      <div
        v-else
        class="key-section mb-24"
      >
        <div class="setup-key pt-24 pb-16">
          <TextWithCopy
            :value="totpSecret"
            :always-show-icon="true"
          />
        </div>
      </div>

      <div class="verification-code mt-8">
        <el-form-item
          :error="error"
          prop="verificationCode"
          :display-error-always="false"
          :inline-message="false"
        >
          <el-input
            v-model="form.verificationCode"
            :placeholder="$t('Enter the 6-digit code from your app')"
            data-testid="verification-code"
            :maxlength="6"
            @input="clearValidation"
          />
        </el-form-item>
      </div>

      <slot
        name="actions"
        :on-submit="handleSubmit"
        :on-cancel="handleCancel"
        :pending="pending"
        :is-valid="isFormValid"
      >
        <div class="default-actions">
          <el-button
            data-testid="cancel"
            fluid
            size="large"
            type="secondary"
            @click="handleCancel"
          >
            {{ $t('Cancel') }}
          </el-button>
          <el-button
            data-testid="submit"
            :disabled="pending || !isFormValid"
            fluid
            native-type="submit"
            size="large"
            type="primary"
            @click="handleSubmit"
          >
            {{ $t('Configure') }}
          </el-button>
        </div>
      </slot>
    </el-form>
  </div>
</template>

<style lang="scss" scoped>
.two-factor-auth-setup {
  background-color: $av-fixed-white;
}

.username {
  font-size: $av-font-size-xs;
  text-align: center;
  color: $av-fixed-light;
}

.description {
  text-align: center;
}

.setup-type {
  display: flex;
  justify-content: center;
}

.qr-section {
  text-align: center;
}

.qr-code {
  display: flex;
  justify-content: center;
}

.setup-key {
  display: flex;
  justify-content: center;
  font-size: $av-font-size-m;
  text-align: center;
}

.scan-instruction {
  font-size: $av-font-size-xs;
}

.verification-code {
  margin-bottom: 16px;
}

.default-actions {
  display: flex;
  gap: 16px;
  width: 100%;
}
</style>
